class W4D_StickyHeader {
	constructor() {
		this.init();
	}

	init() {
		this.header = document.querySelector('.w4d-header');
		if (!this.header) {
			return;
		}

		// Scroll position and header size are read lazily (first scroll frame / ResizeObserver) so init
		// does not force a synchronous style+layout flush in the middle of the DOMContentLoaded handlers.
		this.lastScrollTop = null;
		this.scrollDirection = null;
		this.directionStartScrollTop = null;
		this.headerHeight = null;
		this.headerHeightOffset = null;
		this.deltaDown = 5; // Hide only after a deliberate downward scroll.
		this.deltaUp = 200; // Ignore tiny upward corrections before revealing.
		this.isHidden = this.header.classList.contains('w4d-header--hidden');
		this.ticking = false;

		this.initObservers(); // ResizeObserver delivers the initial header size after layout
		this.bindEvents();
	}

	cacheDimensions() {
		this.headerHeight = this.header.offsetHeight;
		const bodyStyle = window.getComputedStyle(document.body);
		this.headerHeightOffset = parseInt(bodyStyle.getPropertyValue('--header-height'), 10) || this.headerHeight;
	}

	initObservers() {
		// Native ResizeObserver (Baseline 2020) - observe header only to prevent layout reflows on body DOM mutations
		this.resizeObserver = new ResizeObserver(() => {
			this.cacheDimensions();
		});
		this.resizeObserver.observe(this.header);

		// Native IntersectionObserver (Baseline 2019) for top-of-page unstuck detection
		this.sentinel = document.createElement('div');
		this.sentinel.className = 'w4d-header-sentinel';
		this.sentinel.setAttribute('aria-hidden', 'true');
		this.sentinel.style.cssText = 'position:absolute;top:0;left:0;height:1px;width:1px;pointer-events:none;opacity:0;';
		document.body.append(this.sentinel); // appended last: no following siblings to invalidate

		this.topObserver = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					this.setHeaderHidden(false);
					// The sentinel sits at the top of the document, so its offset above the viewport is the scroll
					// position: window.scrollY here forced a synchronous layout on the initial page load.
					this.resetScrollDirection(Math.max(0, Math.round(-entry.boundingClientRect.top)));
				}
			},
			{
				root: null,
				threshold: 0,
			}
		);
		this.topObserver.observe(this.sentinel);
	}

	bindEvents() {
		const onScroll = () => {
			if (!this.ticking) {
				requestAnimationFrame(() => {
					this.handleScroll();
					this.ticking = false;
				});
				this.ticking = true;
			}
		};

		// Passive scroll listener for smooth compositor-thread scrolling
		window.addEventListener('scroll', onScroll, { passive: true });

		// Window resize listener to recalculate viewport-dependent CSS custom properties
		window.addEventListener(
			'resize',
			() => {
				this.cacheDimensions();
				onScroll();
			},
			{ passive: true }
		);
	}

	resetScrollDirection(scrollTop) {
		this.scrollDirection = null;
		this.directionStartScrollTop = scrollTop;
		this.lastScrollTop = scrollTop;
	}

	getScrollDirection(scrollTop) {
		const scrollDirection = scrollTop > this.lastScrollTop ? 'down' : 'up';

		if (scrollDirection !== this.scrollDirection) {
			this.scrollDirection = scrollDirection;
			this.directionStartScrollTop = this.lastScrollTop;
		}

		return scrollDirection;
	}

	setHeaderHidden(isHidden) {
		if (this.isHidden === isHidden && this.header.classList.contains('w4d-header--hidden') === isHidden) {
			return;
		}

		this.isHidden = isHidden;
		this.header.classList.toggle('w4d-header--hidden', isHidden);
		// Consumers read this via the `header-hidden-flag` mixin (local --header-hidden), so only they restyle.
		document.body.classList.toggle('w4d-header-hidden', isHidden);
	}

	handleScroll() {
		if (!this.header.classList.contains('w4d-header--sticky')) {
			return;
		}

		if (document.body.classList.contains('dgwt-wcas-open') || document.body.classList.contains('w4d-overlay-active')) {
			return;
		}

		// Scoped to the header subtree — a document-wide complex query here ran on every scroll frame.
		if (this.header.querySelector('nav:not([data-header-type="3"]) .nav__links-wrapper .nav__item--submenu-open')) {
			return;
		}

		const scrollTop = Math.max(0, window.scrollY);

		if (this.headerHeight === null) {
			this.cacheDimensions();
		}

		if (this.lastScrollTop === null) {
			this.resetScrollDirection(scrollTop);
		}

		// Keep header visible while original position remains in view
		if (scrollTop <= this.headerHeightOffset) {
			this.setHeaderHidden(false);
			this.resetScrollDirection(scrollTop);
			return;
		}

		if (scrollTop === this.lastScrollTop) {
			return;
		}

		// Measure continuous movement from direction change
		const scrollDirection = this.getScrollDirection(scrollTop);
		const scrollDistance = Math.abs(scrollTop - this.directionStartScrollTop);

		if (scrollDirection === 'down' && scrollTop > this.headerHeight && scrollDistance > this.deltaDown) {
			this.setHeaderHidden(true);
		} else if (scrollDirection === 'up' && scrollDistance > this.deltaUp) {
			this.setHeaderHidden(false);
		}

		this.lastScrollTop = scrollTop;
	}
}

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => new W4D_StickyHeader());
} else {
	new W4D_StickyHeader();
}
